import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { GapList } from '@/components/GapList';
import { ViewToggle, type GapView } from '@/components/ViewToggle';
import { AppShell } from '@/components/layout/AppShell';
import { useGaps } from '@/hooks/use-gaps';
import { useDrafts } from '@/hooks/use-drafts';
import { useGenerate } from '@/hooks/use-generate';
import { useFeedback } from '@/hooks/use-feedback';
import { usePublishedCount } from '@/hooks/use-published-count';
import { useTriage } from '@/hooks/use-triage';
import { TriageTabs } from '@/components/TriageTabs';
import type { DraftFormat, FeedbackPayload, TriageStatus } from '@/lib/api';
import { showGenerateOutcome } from '@/lib/generate-outcome';
import { getToken } from '@/lib/auth';

export const Route = createFileRoute('/websites/$websiteId/gaps')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/connect' });
  },
  component: GapsPage,
});

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg bg-secondary px-4 py-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xl font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function GapsPage() {
  const { websiteId } = Route.useParams();
  const navigate = useNavigate();
  const gaps = useGaps(websiteId);
  const drafts = useDrafts(websiteId);
  const generate = useGenerate(websiteId);
  const feedback = useFeedback(websiteId);
  const publishedCount = usePublishedCount(websiteId);
  const triage = useTriage(websiteId);
  const [pendingGapId, setPendingGapId] = useState<string | null>(null);
  const [feedbackPendingId, setFeedbackPendingId] = useState<string | null>(null);
  const [view, setView] = useState<GapView>('cards');
  const [triageTab, setTriageTab] = useState<TriageStatus>('neu');

  function onSaveFeedback(gapId: string, fb: FeedbackPayload) {
    setFeedbackPendingId(gapId);
    feedback.mutate(
      { trendMatchId: gapId, ...fb },
      {
        onSuccess: () => toast.success('Bewertung gespeichert'),
        onError: (e: Error) => toast.error(e.message),
        onSettled: () => setFeedbackPendingId(null),
      },
    );
  }

  function onTriage(gapId: string, status: TriageStatus) {
    triage.mutate(
      { trendMatchId: gapId, status },
      {
        onSuccess: () => toast.success('Triage gespeichert'),
        onError: (e: Error) => toast.error(e.message),
      },
    );
  }

  function onGenerate(gapId: string, format: DraftFormat, force = false) {
    setPendingGapId(gapId);
    generate.mutate(
      { gapId, format, force },
      {
        onSuccess: (res) =>
          showGenerateOutcome(res, {
            goToDraft: (draftId) =>
              navigate({ to: '/drafts/$draftId', params: { draftId } }),
            notifyCreated: () => toast.success('Entwurf erstellt'),
            notifyDuplicate: (trendTitle, percent, onRegenerate) =>
              toast(`Bedeutungsgleicher Entwurf: „${trendTitle}" (${percent} %)`, {
                description:
                  'Es existiert bereits ein Entwurf zu einem fast identischen Trend.',
                action: { label: 'Trotzdem neu erzeugen', onClick: onRegenerate },
              }),
            regenerate: () => onGenerate(gapId, format, true),
          }),
        onError: (e: Error) => toast.error(e.message),
        onSettled: () => setPendingGapId(null),
      },
    );
  }

  // Triage filter
  const allGaps = gaps.isSuccess ? gaps.data : [];
  const counts = {
    neu: allGaps.filter((g) => g.triage_status === 'neu').length,
    verfolgen: allGaps.filter((g) => g.triage_status === 'verfolgen').length,
    ignoriert: allGaps.filter((g) => g.triage_status === 'ignoriert').length,
  };
  const shownGaps = allGaps.filter((g) => g.triage_status === triageTab);

  // Computed stats from loaded data
  const openGaps = gaps.isSuccess ? gaps.data.filter((g) => !g.learned_irrelevant) : [];
  const avgRelevanz =
    openGaps.length > 0
      ? (openGaps.reduce((sum, g) => sum + g.relevance_score, 0) / openGaps.length)
          .toFixed(1)
          .replace('.', ',')
      : '–';
  const draftsCount = drafts.data?.length ?? 0;
  const publishedValue = publishedCount.isSuccess ? publishedCount.data : '–';

  return (
    <AppShell websiteId={websiteId} active="gaps">
      <div className="mx-auto max-w-4xl p-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Content-Lücken</h1>
          <ViewToggle value={view} onChange={setView} />
        </div>

        {/* Stat tiles */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Offene Lücken" value={gaps.isSuccess ? openGaps.length : '–'} />
          <StatTile label="Ø Relevanz" value={gaps.isSuccess ? avgRelevanz : '–'} />
          <StatTile label="Entwürfe" value={drafts.isSuccess ? draftsCount : '–'} />
          <StatTile label="Veröffentlicht" value={publishedValue} />
        </div>

        {/* Triage tabs */}
        <div className="mb-4">
          <TriageTabs value={triageTab} counts={counts} onChange={setTriageTab} />
        </div>

        {/* Gap list */}
        {gaps.isLoading && <Skeleton className="h-40 w-full" />}
        {gaps.isError && (
          <p className="text-destructive">Lücken konnten nicht geladen werden.</p>
        )}
        {gaps.isSuccess && (
          <GapList
            gaps={shownGaps}
            view={view}
            onGenerate={onGenerate}
            pendingGapId={pendingGapId}
            onSaveFeedback={onSaveFeedback}
            feedbackPendingId={feedbackPendingId}
            onTriage={onTriage}
          />
        )}
      </div>
    </AppShell>
  );
}
